import { FileValue } from '@8base-react/file-input';
import { css, Theme } from '@emotion/react';
import { Edit, Person } from '@mui/icons-material';
import { Skeleton, Typography } from '@mui/material';
import { useMemo } from 'react';

import { extractYear, formatUserName, processFilestackUrlSrcSet } from '@jebel/utils';

import { AvatarImageInput, Box, LoadingOverlay } from 'shared/components/ui';
import { CurrentUserFragment, UserUpdateInput, useUserUpdateMutation } from 'shared/graphql';
import { useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

const USER_AVATAR_SIZE = 90;

const avatarCss = css`
  width: ${USER_AVATAR_SIZE}px;
  height: ${USER_AVATAR_SIZE}px;
`;

const userNameCss = css`
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const editIconCss = css`
  font-size: 16px;
`;

const containerCss = (theme: Theme) => css`
  display: grid;
  align-items: center;
  align-content: center;
  justify-items: center;
  justify-content: center;
  gap: 1rem;
  padding-top: 2.5rem;
  padding-bottom: 1rem;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  border-bottom-color: ${theme.palette.divider};
`;

const userNameContainerCss = css`
  display: grid;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 20px;
`;

const userGraduatingYearCss = (theme: Theme) => css`
  display: grid;
  grid-template-columns: auto auto;
  justify-items: center;
  justify-content: center;
  align-items: center;
  align-content: center;
  grid-column-gap: 4px;
  color: ${theme.palette.text.light};
`;

interface Props {
  user: CurrentUserFragment | undefined | null;
  loading: boolean;
}

const MESSAGE_UPDATING_AVATAR_KEY = 'UPDATING_AVATAR';

export function MemberSidebarUserProfile({ user, loading }: Props) {
  const [mutateUser, { loading: updating }] = useUserUpdateMutation({
    refetchQueries: ['UserById'],
    awaitRefetchQueries: true,
  });

  const { showError, showSuccess, showMessage, dismiss } = useToast();

  const userGraduatingYear = useMemo(() => {
    return extractYear(user?.graduatingYear);
  }, [user?.graduatingYear]);

  const fullname = useMemo(() => formatUserName(user), [user]);

  const avatarSource = useMemo(() => {
    return user?.avatar?.downloadUrl || undefined;
  }, [user?.avatar?.downloadUrl]);

  const handleChangeAvatar = async (avatar: FileValue | null) => {
    if (!user?.id) {
      return;
    }

    showMessage(`Updating user's avatar, please stand by.`, { id: MESSAGE_UPDATING_AVATAR_KEY });

    try {
      const payload: UserUpdateInput = {
        id: user.id,
      };

      if (!avatar && user?.avatar) {
        // User wants to remove the previous avatar
        payload.avatar = { disconnect: { fileId: user.avatar.fileId } };
      }

      if (avatar) {
        // User wants to update the avatar
        payload.avatar = {
          create: {
            fileId: avatar.fileId,
            filename: avatar.filename,
            public: true,
          },
        };
      }

      // Update the user avatar
      // https://github.com/jebelapp/jebel/issues/1790
      await mutateUser({ variables: { data: payload } });

      showSuccess('Avatar was successfully updated');
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message);
      }
    } finally {
      dismiss(MESSAGE_UPDATING_AVATAR_KEY);
    }
  };

  return (
    <Box css={containerCss}>
      <LoadingOverlay
        loading={loading}
        overlay={<Skeleton variant="circular" width={USER_AVATAR_SIZE} height={USER_AVATAR_SIZE} />}
      >
        <AvatarImageInput
          src={avatarSource}
          loading={updating}
          alt={fullname}
          css={avatarCss}
          onChange={handleChangeAvatar}
          srcSet={processFilestackUrlSrcSet(avatarSource, {
            compress: true,
            resize: {
              width: USER_AVATAR_SIZE,
              height: USER_AVATAR_SIZE,
            },
          })}
        >
          <Person fontSize="inherit" />
        </AvatarImageInput>
      </LoadingOverlay>

      <Box css={userNameContainerCss}>
        <LoadingOverlay loading={loading}>
          <Typography
            css={userNameCss}
            variant="inherit"
            align="inherit"
            color="initial"
            paragraph={false}
          >
            {fullname}
          </Typography>
        </LoadingOverlay>
      </Box>

      {Boolean(userGraduatingYear) && (
        <Box css={userGraduatingYearCss}>
          <Typography variant="inherit" align="inherit" color="initial" paragraph={false}>
            CLASS OF {userGraduatingYear}
          </Typography>

          <Edit css={editIconCss} />
        </Box>
      )}
    </Box>
  );
}
